'use client';

import { createContext, useContext, useState, type ReactNode } from 'react';

type Language = 'de' | 'en' | 'es' | 'fr';

interface Translations {
  [key: string]: {
    [K in Language]: string;
  };
}

const translations: Translations = {
  exclusiveLine: {
    de: 'Exklusiv beim Kauf von Era 100 Pro und nur für autorisierte Sonos Partner:',
    en: 'Exclusive with purchase of Era 100 Pro and only for authorized Sonos partners:',
    es: 'Exclusivo con la compra de Era 100 Pro y solo para socios autorizados de Sonos:',
    fr: "Exclusif à l'achat d'un Era 100 Pro et uniquement pour les partenaires Sonos autorisés :",
  },
  legalStreamingHeadline: {
    de: 'Legales Musik‑Streaming für Unternehmen – Soundsuit 3 Monate kostenlos testen*',
    en: 'Legal music streaming for businesses – Try Soundsuit 3 months free*',
    es: 'Streaming de música legal para empresas – Prueba Soundsuit 3 meses gratis*',
    fr: 'Streaming musical légal pour les entreprises – Essayez Soundsuit 3 mois gratuitement*',
  },
  partnerButton: {
    de: 'Partner werden',
    en: 'Become a partner',
    es: 'Conviértete en socio',
    fr: 'Devenir partenaire',
  },
  testNowButton: {
    de: 'Jetzt testen',
    en: 'Try now',
    es: 'Probar ahora',
    fr: 'Essayer maintenant',
  },
  didYouKnow: {
    de: 'Wusstest du schon?',
    en: 'Did you know?',
    es: '¿Sabías que?',
    fr: 'Le saviez-vous ?',
  },
  didYouKnowText: {
    de: 'Viele Unternehmen spielen Musik in ihren Geschäften über ihre privaten Streaming-Konten. Dies ist nicht für die kommerzielle Nutzung lizenziert und kann zu hohen Geldstrafen führen.',
    en: 'Many businesses play music in their stores through their private streaming accounts. This is not licensed for commercial use and can lead to high fines.',
    es: 'Muchas empresas reproducen música en sus tiendas a través de sus cuentas de streaming privadas. Esto no tiene licencia para uso comercial y puede llevar a multas altas.',
    fr: "De nombreuses entreprises diffusent de la musique dans leurs magasins via leurs comptes de streaming privés. Ce n'est pas licencié pour un usage commercial et peut entraîner de lourdes amendes.",
  },
  ourOffer: {
    de: 'Unser Angebot für dich',
    en: 'Our offer for you',
    es: 'Nuestra oferta para ti',
    fr: 'Notre offre pour vous',
  },
  ourOfferText: {
    de: 'Mit unserem exklusiven Angebot unterstützen Sie Ihre Kunden beim rechtssicheren Musikstreaming – und positionieren sich gleichzeitig als kompetenter Problemlöser mit echtem Mehrwert.',
    en: 'With our exclusive offer, you support your customers with legally secure music streaming – and position yourself as a competent problem solver with real added value.',
    es: 'Con nuestra oferta exclusiva, apoyas a tus clientes con streaming de música legalmente seguro – y te posicionas como un solucionador de problemas competente con valor real añadido.',
    fr: "Avec notre offre exclusive, vous aidez vos clients à diffuser de la musique en toute légalité — et vous vous positionnez comme un expert capable d'apporter une réelle valeur ajoutée.",
  },
  threeMonthsFree: {
    de: 'Unser exklusives Angebot – Soundsuit 3 Monate lang kostenlos testen',
    en: 'Our exclusive offer – Try Soundsuit free for 3 months',
    es: 'Nuestra oferta exclusiva: prueba Soundsuit gratis durante 3 meses',
    fr: 'Notre offre exclusive – Essayez Soundsuit gratuitement pendant 3 mois',
  },
  threeMonthsText: {
    de: 'Beim Kauf von mind. einem Paar Sonos Era 100 Pro erhältst du für deine Kund:innen einen kostenlosen Testzugang zu Soundsuit für 3 Monate (statt regulär 30 Tage). So hilfst du ihnen, Musik rechtlich abgesichert zu streamen – und positionierst dich als kompetente:r Problemlöser:in mit echtem Mehrwert.',
    en: 'When you purchase at least one pair of Sonos Era 100 Pro, your customers receive a free 3‑month Soundsuit trial (instead of the usual 30 days). You help them stream legally for business and position yourself as a trusted problem solver with real added value.',
    es: 'Al comprar al menos un par de Sonos Era 100 Pro, tus clientes reciben una prueba gratuita de Soundsuit de 3 meses (en lugar de los 30 días habituales). Les ayudas a reproducir música de forma legal para negocios y te posicionas como un solucionador de problemas de confianza con verdadero valor añadido.',
    fr: "Pour l'achat d'au moins une paire de Sonos Era 100 Pro, vos clients bénéficient d'un essai Soundsuit gratuit de 3 mois (au lieu des 30 jours habituels). Vous les aidez à diffuser légalement en contexte professionnel et vous positionnez comme un partenaire de confiance à forte valeur ajoutée.",
  },
  millionsLicensed: {
    de: 'Millionen lizenzierte Titel kuratiert für die gewerbliche Nutzung',
    en: 'Millions of licensed tracks curated for commercial use',
    es: 'Millones de títulos con licencia curados para uso comercial',
    fr: 'Des millions de titres sous licence, sélectionnés pour un usage professionnel',
  },
  millionsLicensedText: {
    de: 'Von internationalen Hits bis zu Nischen‑Songs – aus allen Genres und Jahrzehnten – kuratiert für die geschäftliche Nutzung.',
    en: 'From international hits to niche songs — across all genres and decades — curated for business use.',
    es: 'Desde éxitos internacionales hasta canciones de nicho — de todos los géneros y décadas — curados para uso empresarial.',
    fr: 'Des succès internationaux aux titres de niche — tous genres et décennies confondus — sélectionnés pour un usage en entreprise.',
  },
  easyControl: {
    de: 'Einfache Steuerung mit der Soundsuit-App',
    en: 'Easy control with the Soundsuit app',
    es: 'Control sencillo con la app de Soundsuit',
    fr: "Contrôle simple avec l'application Soundsuit",
  },
  easyControlText: {
    de: 'Erstelle deine eigenen Playlists und plane deren Wiedergabe direkt im Kalender. Verwalte mehrere Standorte und Zonen zentral und remote.',
    en: 'Intuitive Soundsuit app to manage music: create playlists and schedule playback right from your calendar.',
    es: 'App de Soundsuit intuitiva para gestionar música: crea listas y programa la reproducción desde el calendario.',
    fr: 'Application Soundsuit intuitive pour gérer la musique : créez des playlists et planifiez la lecture depuis votre calendrier.',
  },
  whatMustYouDo: {
    de: 'Was musst du tun?',
    en: 'What must you do?',
    es: '¿Qué debes hacer?',
    fr: 'Que devez‑vous faire ?',
  },
  step1: {
    de: 'Als autorisierter Partner von Sonos bestellst du mindestens ein Paar Sonos Era 100 Pro.',
    en: 'As an authorized Sonos partner, order at least one pair of Sonos Era 100 Pro.',
    es: 'Como socio autorizado de Sonos, pide al menos un par de Sonos Era 100 Pro.',
    fr: 'En tant que partenaire Sonos autorisé, commandez au moins une paire de Sonos Era 100 Pro.',
  },
  step2: {
    de: 'Mit der Lieferung deiner Era 100 Pro‑Bestellung erhältst du eine Karte mit einem Aktions‑Code. Diesen Code benötigst du für die Registrierung bei Soundsuit und zur Freischaltung des kostenfreien Testzugangs (gültig für 3 Monate).',
    en: 'With delivery of your Era 100 Pro order, you receive a card with a promo code. You’ll use this code to register with Soundsuit and activate the free trial (valid for 3 months).',
    es: 'Con la entrega de tu pedido de Era 100 Pro recibirás una tarjeta con un código promocional. Usarás este código para registrarte en Soundsuit y activar la prueba gratuita (válida por 3 meses).',
    fr: 'À la livraison de votre commande Era 100 Pro, vous recevrez une carte avec un code promotionnel. Ce code sert à vous inscrire sur Soundsuit et à activer l’essai gratuit (valable 3 mois).',
  },
  step3: {
    de: 'Fügen Sie Soundsuit zum Sonos-System hinzu.',
    en: 'Add Soundsuit to the Sonos system.',
    es: 'Agrega Soundsuit al sistema Sonos.',
    fr: 'Ajoutez Soundsuit au système Sonos.',
  },
  step4: {
    de: 'Ihr Kunde testet Soundsuit 3 Monate lang kostenlos (statt der regulären 30 Tage). Das Abonnement endet nach Ablauf dieser Testphase automatisch. Sie müssen nichts weiter tun.',
    en: "Your customer tests Soundsuit for 3 months free (instead of the regular 30 days). The subscription ends automatically after this trial phase expires. You don't need to do anything else.",
    es: 'Tu cliente prueba Soundsuit durante 3 meses gratis (en lugar de los 30 días regulares). La suscripción termina automáticamente después de que expire esta fase de prueba. No necesitas hacer nada más.',
    fr: "Votre client teste Soundsuit gratuitement pendant 3 mois (au lieu des 30 jours habituels). L'abonnement prend fin automatiquement à l'issue de cette période d'essai. Vous n'avez rien d'autre à faire.",
  },
  step5: {
    de: 'Mit der intuitiven Soundsuit-App kann Ihr Kunde Playlists erstellen und die Wiedergabe planen – sofort verfügbar über Sonos.',
    en: 'With the intuitive Soundsuit app, your customer can create playlists and schedule playback – immediately available via Sonos.',
    es: 'Con la app Soundsuit intuitiva, tu cliente puede crear listas de reproducción y programar la reproducción – inmediatamente disponible a través de Sonos.',
    fr: "Avec l'application Soundsuit intuitive, votre client peut créer des playlists et planifier la lecture — immédiatement disponible via Sonos.",
  },
  noHiddenConditions: {
    de: 'Keine versteckten Bedingungen:',
    en: 'No hidden conditions:',
    es: 'Sin condiciones ocultas:',
    fr: 'Aucune condition cachée :',
  },
  noHiddenConditionsText: {
    de: 'Keine Kreditkarte erforderlich, das Abonnement endet automatisch nach Ablauf der Testphase. Ohne weitere Bedingungen.',
    en: 'No credit card required, the subscription ends automatically after the trial phase expires. No additional conditions.',
    es: 'No se requiere tarjeta de crédito, la suscripción termina automáticamente después de que expire la fase de prueba. Sin condiciones adicionales.',
    fr: "Aucune carte bancaire requise. L'abonnement prend fin automatiquement à l'issue de la période d'essai. Aucune condition supplémentaire.",
  },
  offerValid: {
    de: 'Angebot gültig bis 31. Januar 2026.',
    en: 'Offer valid until January 31, 2026.',
    es: 'Oferta válida hasta el 31 de enero de 2026.',
    fr: 'Offre valable jusqu’au 31 janvier 2026.',
  },
  codesValid: {
    de: 'Codes müssen bis 28. Februar 2026 eingelöst werden.',
    en: 'Codes must be redeemed by April 30, 2026.',
    es: 'Los códigos deben canjearse antes del 28 de febrero de 2026.',
    fr: 'Les codes doivent être utilisés avant le 30 avril 2026.',
  },
};

interface TranslationContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  availableLanguages: Array<{ code: Language; name: string; flag: string }>;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

export function TranslationProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>('de');

  const availableLanguages = [
    { code: 'de' as Language, name: 'Deutsch', flag: '🇩🇪' },
    { code: 'en' as Language, name: 'English', flag: '🇺🇸' },
    { code: 'es' as Language, name: 'Español', flag: '🇪🇸' },
    { code: 'fr' as Language, name: 'Français', flag: '🇫🇷' },
  ];

  const t = (key: string): string => {
    return translations[key]?.[language] || key;
  };

  return (
    <TranslationContext.Provider value={{ language, setLanguage, t, availableLanguages }}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslations() {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslations must be used within a TranslationProvider');
  }
  return context;
}
